import React, { useEffect, useState } from "react";
import "./ConsentPage.css";
import { useNavigate } from "react-router-dom";
import Spinner from "react-bootstrap/Spinner";

import { getSessionParams } from "../session/utils/sessionStorage";
import { handleRedirectBack } from "../../shared/utils/navigationUtils";
import { rejectSession } from "../session/utils/rejectSession";
import SessionCountdown from "../session/components/SessionCountdown";
import { logger } from "../../shared/utils/logger";
import { apiFetch } from "../../shared/utils/http";
import { useTranslation } from "react-i18next";

interface UserClaims {
  method?: string;

  email?: string;
  firstName?: string;
  lastName?: string;
  PublicServiceNumber?: string;
  BirthDate?: string;

  given_name?: string;
  family_name?: string;
  birthdate?: string;
  dob?: string;
  PPSN?: string;
  ppsn?: string;
  sub?: string;
}

interface UserData {
  email: string;
  firstName: string;
  lastName: string;
  ppsn: string;
  birthDate: string;
}

const logPrefix = "[Authorization]";

function claimsToUserData(claims: UserClaims): UserData {
  return {
    email: claims.email ?? "",
    firstName: claims.firstName ?? claims.given_name ?? "",
    lastName: claims.lastName ?? claims.family_name ?? "",
    ppsn:
      claims.PublicServiceNumber ??
      (claims as any).PPSN ??
      (claims as any).ppsn ??
      (claims as any).sub ??
      "",
    birthDate: claims.BirthDate ?? claims.birthdate ?? claims.dob ?? "",
  };
}

function isDateDDMMYYYY(date: string): boolean {
  return /^\d{2}\/\d{2}\/\d{4}$/.test(date);
}

function toDDMMYYYY(input: string | undefined): string {
  if (!input) return "";
  if (isDateDDMMYYYY(input)) return input;

  const d = new Date(input);
  if (isNaN(d.getTime())) return input;
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

const ConsentPopup: React.FC = () => {
  const navigate = useNavigate();

  const [userData, setUserData] = useState<UserData | null>(null);
  const [authMethod, setAuthMethod] = useState<string | null>(null);

  const [isDataValid, setIsDataValid] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [processing, setProcessing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [modalVisible] = useState<boolean>(true);
  const [cancelClicked, setCancelClicked] = useState<boolean>(false);
  const { t } = useTranslation();

  const readJsonSafe = async (resp: Response) => {
    const ct = (resp.headers.get("content-type") || "").toLowerCase();
    if (!ct.includes("application/json")) {
      logger.warn(
        `${logPrefix} Expected JSON but got content-type="${ct}" (possible SPA fallback)`
      );
      return null;
    }
    if (resp.status === 204) return null;
    try {
      return await resp.json();
    } catch (e) {
      logger.error(`${logPrefix} JSON parse failed:`, e);
      return null;
    }
  };

  const getUserClaimsFromServer = async (): Promise<UserClaims | null> => {
    logger.debug(`${logPrefix} GET /session/me …`);
    const res = await apiFetch("/session/me", { credentials: "include" });
    logger.debug(`${logPrefix} /session/me ->`, {
      status: res.status,
      statusText: res.statusText,
      ct: res.headers.get("content-type") || "",
    });

    if (!res.ok) {
      if (res.status === 401) {
        setError(t("consent.error.notAuthenticated"));
      } else {
        setError(t("consent.error.failedToLoadProfile", { status: res.status }));
      }
      return null;
    }

    const data = await readJsonSafe(res);
    if (!data) return null;

    return data as UserClaims;
  };

  const checkAuthorizationStatus = async (user: UserData): Promise<boolean> => {
    logger.info(`${logPrefix} Checking authorization for:`, {
      email: !!user.email,
      firstName: !!user.firstName,
      lastName: !!user.lastName,
      ppsn: !!user.ppsn,
      birthDate: !!user.birthDate,
    });

    const sp = getSessionParams();
    if (!sp?.sessionId) {
      logger.warn(`${logPrefix} Missing sessionId.`);
      setError(t("consent.error.sessionIdMissing"));
      return false;
    }

    const query = new URLSearchParams({
      session: sp.sessionId,
      identityMatchingEnabled: "true",
    }).toString();

    const body = {
      email: user.email,
      givenName: user.firstName,
      firstName: user.firstName,
      lastName: user.lastName,
      ppsn: user.ppsn,
      birthDate: toDDMMYYYY(user.birthDate),
    };

    const maxRetries = 10;
    let attempt = 0;

    while (attempt < maxRetries) {
      const tryNo = attempt + 1;

      try {
        logger.info(
          `${logPrefix} POST /proxy/checkAuthorized?${query} (attempt ${tryNo}/${maxRetries})`
        );

        const resp = await apiFetch(
          `/proxy/checkAuthorized?${query}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify(body),
            credentials: "include",
          },
          { autoLogoutOn401: false, throwOn401: false }
        );

        logger.info(`${logPrefix} /proxy/checkAuthorized ->`, {
          status: resp.status,
          statusText: resp.statusText,
        });

        if (resp.status === 200) return true;

        if ([202, 204, 503, 504].includes(resp.status)) {
          logger.info(
            `${logPrefix} Pending/retryable status ${resp.status} (attempt ${tryNo}/${maxRetries})`
          );
        } else if (resp.status === 401) {
          setError(t("consent.error.unauthorizedAccess"));
          return false;
        } else if (resp.status === 404) {
          setError(t("consent.error.documentNotFound"));
          return false;
        } else {
          const maybeJson = await readJsonSafe(resp);
          logger.error(`${logPrefix} Unexpected checkAuthorized response`, resp.status, maybeJson);
          setError(t("consent.error.unexpected"));
          return false;
        }
      } catch (e) {
        logger.warn(`${logPrefix} checkAuthorized failed (attempt ${tryNo}/${maxRetries})`, e);
      }

      attempt++;
      if (attempt < maxRetries) {
        logger.info(`${logPrefix} Waiting 5s before next retry…`);
        await new Promise((r) => setTimeout(r, 5000));
      }
    }

    setError(t("consent.error.authorizationTakingLong"));
    return false;
  };

  useEffect(() => {
    (async () => {
      try {
        logger.info(`${logPrefix} init — begin`);
        setLoading(true);

        const tResp = await apiFetch("/session/token", { credentials: "include" });
        logger.debug(`${logPrefix} GET /session/token ->`, {
          status: tResp.status,
          statusText: tResp.statusText,
          ct: tResp.headers.get("content-type") || "",
        });

        if (!tResp.ok) {
          setError(t("consent.error.noActiveSessionRedirect"));
          setTimeout(() => navigate("/login"), 1500);
          return;
        }

        const tJson = await readJsonSafe(tResp);
        const tokenPresent = !!tJson?.token;
        if (!tokenPresent) {
          logger.warn(`${logPrefix} No token found in server session.`);
          setError(t("consent.error.noSessionRedirect"));
          setTimeout(() => navigate("/login"), 1500);
          return;
        }

        const claims = await getUserClaimsFromServer();
        logger.debug(`${logPrefix} claims from server:`, claims);
        if (!claims) return;

        const method = (claims as any).method ?? null;
        setAuthMethod(method);
        const isEidas = method === "EIDAS";

        const ud = claimsToUserData(claims);

        if (!isEidas) {
          ud.birthDate = toDDMMYYYY(ud.birthDate);
        }

        setUserData(ud);

        const requiredOk = isEidas
          ? Boolean(ud.firstName && ud.birthDate && ud.ppsn)
          : Boolean(ud.email && ud.firstName && ud.birthDate && ud.ppsn);

        setIsDataValid(requiredOk);

        if (!requiredOk) {
          logger.warn(`${logPrefix} Missing required user fields`);
          setError(t("consent.error.missingRequiredInfo"));
          return;
        }

        logger.info(`${logPrefix} user data ready`);
      } catch (e) {
        logger.error(`${logPrefix} init error:`, e);
        setError(t("consent.error.failedAuthenticationRedirect"));
        setTimeout(() => navigate("/login"), 1500);
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const handleConsent = async () => {
    if (!isDataValid || !userData) {
      logger.warn("[Consent] Invalid/missing user data.");
      setError(t("consent.error.noVerifiedAccount"));
      return;
    }

    logger.info("[Consent] User clicked Consent → re-checking authorization…");
    setError(null);
    setProcessing(true);
    setLoading(true);

    const authorized = await checkAuthorizationStatus(userData);
    if (authorized) {
      logger.info("[Consent] Authorized. Navigating to preview space.");
      navigate("/previewspace");
    } else {
      logger.warn("[Consent] Not authorized.");
    }

    setProcessing(false);
    setLoading(false);
  };

  const handleCancel = async () => {
    logger.info("[Consent] Cancel clicked. Attempting to reject session.");
    setProcessing(true);
    setCancelClicked(true);

    const success = await rejectSession(navigate);
    if (success) {
      logger.info("[Consent] Session rejected. Redirecting back…");
      await handleRedirectBack(navigate, true);
    } else {
      logger.warn("[Consent] Failed to reject session.");
    }
  };

  const handleRedirect = async () => {
    logger.info("[Consent] Redirect back (error path).");
    setProcessing(true);
    setCancelClicked(true);
    await handleRedirectBack(navigate, true);
  };

  const isEidas = authMethod === "EIDAS";

  return (
    <div>
      <SessionCountdown />
      <div className="overlay">
        {modalVisible && (
          <div className="popup" id="BOX_CONSENT_POPUP">
            <h2 className="header" id="TXT_CONSENT_HEADER">
              {t("consent.header")}
            </h2>

            {error && (
              <p style={{ color: "red" }} id="TXT_CONSENT_ERROR_MESSAGE">
                {error}
              </p>
            )}

            {loading && (
              <div className="loading-screen" id="BOX_CONSENT_LOADING_SCREEN">
                <Spinner animation="border" role="status" id="SPINNER_CONSENT_LOADING" />
                <p id="TXT_CONSENT_LOADING">{t("consent.loading.checkingAuthorization")}</p>
              </div>
            )}

            {processing && cancelClicked && (
              <div className="loading-screen" id="BOX_CANCEL_LOADING_SCREEN">
                <Spinner animation="border" role="status" id="SPINNER_CANCEL_REDIRECT" />
                <p id="TXT_CONSENT_REDIRECT">{t("consent.loading.redirectingRequesterPortal")}</p>
              </div>
            )}

            {!loading && !processing && !error && (
              <>
                <p className="text" id="TXT_CONSENT_DESCRIPTION">
                  {t("consent.description")}
                </p>

                {userData && (
                  <div id="BOX_USER_DATA">
                    <p id="TXT_FIRSTNAME">
                      <strong>{t("consent.user.firstName")}</strong> {userData.firstName}
                    </p>
                    <p id="TXT_LASTNAME">
                      <strong>{t("consent.user.lastName")}</strong> {userData.lastName}
                    </p>

                    {!isEidas && (
                      <p id="TXT_EMAIL">
                        <strong>{t("consent.user.email")}</strong> {userData.email}
                      </p>
                    )}

                    <p id="CAL_BIRTHDATE">
                      <strong>{t("consent.user.dateOfBirth")}</strong> {userData.birthDate}
                    </p>

                    <p id="TXT_PPSN">
                      <strong>{isEidas ? t("consent.user.eidasIdentifier") : t("consent.user.uniqueIdentifier")}</strong>{" "}
                      {userData.ppsn}
                    </p>

                    <p id="TXT_CONSENT_DISCLAIMER">
                      {t("consent.disclaimer")}
                    </p>
                  </div>
                )}

                <div className="buttonContainer" id="BOX_CONSENT_BUTTON_CONTAINER">
                  <button
                    className="cancelButton"
                    id="BTN_CANCEL"
                    onClick={handleCancel}
                    disabled={processing}
                  >
                    {t("consent.cancel")}
                  </button>

                  <button
                    className="selectButton"
                    id="BTN_CONSENT"
                    onClick={handleConsent}
                    disabled={loading || processing || !isDataValid}
                  >
                    {t("consent.confirm")}
                  </button>
                </div>
              </>
            )}

            {!loading && !processing && error && (
              <button
                className="cancelButton"
                id="BTN_ERROR_CANCEL"
                onClick={handleRedirect}
                disabled={processing}
              >
                {t("consent.returnToRequesterPortal")}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConsentPopup;
